'use client';
import { useParams, useSearchParams } from 'next/navigation';
import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';

import { REMINDER_STORAGE_READY } from '~constants/events';
import { routes } from '~constants/routes';
import { QUERY_TOKEN_PARAM_NAME } from '~constants/token';
import { useDialogs } from '~entities/dialogs';
import { CheckoutReason } from '~features/annals';
import { annals } from '~features/annals/hooks';
import {
	getPaywallReason,
	paywallLinkParam,
	useNeedsToShowBotPaywall,
	useNeedsToShowNeuronsPaywall,
	useNeedsToShowPaywall,
} from '~features/paywall';
import {
	handlePromocodeFromQuery,
	QUERY_PROMOCODE_PARAM_NAME,
} from '~features/promocode';
import {
	loadFromSessionStorage,
	saveToSessionStorage,
	ssKeys,
} from '~features/storage';
import { useLastBotId, useSetLastBotId } from '~hooks/useLastBotId';
import { useSession } from '~hooks/useSession';
import { usePathname, useRouter } from '~i18n/navigation';
import { MarkersService } from '~services/markers';
import type { AppDispatch } from '~store/index';
import { AppOpenedFromEmailAction } from '~store/utils/track-email-redirect-event';
import { NextUrlConstructor } from '~utils/NextUrlConstructor';

const fireReminderEvent = () => {
	document.dispatchEvent(
		new CustomEvent(REMINDER_STORAGE_READY, {
			detail: {
				ready: true,
			},
		})
	);
};

type Options = {
	paywall?: (typeof paywallLinkParam)[keyof typeof paywallLinkParam];
};

export const useLandingQuery = (options?: Options) => {
	const { paywall } = options || {};

	const pathname = usePathname();
	const searchParams = useSearchParams();
	const router = useRouter();
	const { userId, isDemoUser } = useSession();
	const { recipientId: botClientId } = useParams<{ recipientId?: string }>();

	const { bots, unavailableBots, isSuccess } = useDialogs();

	const handledRef = useRef(false);
	const dispatch = useDispatch<AppDispatch>();

	const setLastBotId = useSetLastBotId();
	const { isSuccess: isLastBotIdSuccess, lastBotId } = useLastBotId();
	const { setNeedsToShowBotPaywall } = useNeedsToShowBotPaywall();
	const { setNeedsToShowPaywall } = useNeedsToShowPaywall();
	const { setNeedsToShowNeuronsPaywall } = useNeedsToShowNeuronsPaywall();

	const annalsSiteVisitDemoUser = annals.auth.useSiteVisitDemoUser();

	useEffect(() => {
		if (
			!isDemoUser ||
			loadFromSessionStorage(ssKeys.ANNALS_SITE_VISIT_SENT, false)
		) {
			return;
		}

		const markers = MarkersService.getMarkersAnnals();
		annalsSiteVisitDemoUser({ ...markers, id: userId });
		saveToSessionStorage(ssKeys.ANNALS_SITE_VISIT_SENT, true);
	}, [annalsSiteVisitDemoUser, isDemoUser, userId]);

	useEffect(() => {
		if (!userId || !isSuccess || !isLastBotIdSuccess || handledRef.current) {
			return;
		}

		// TODO: remove.
		// At this moment, we could receive non-RFC compliant links.
		// More info in thread
		// https://datinggrp.slack.com/archives/C053Q3S4PEX/p1722302459942029?thread_ts=1722302232.548779&cid=C053Q3S4PEX
		let trueQuery: URLSearchParams | undefined = undefined;

		const qRecipientId = searchParams.get('recipientId');
		if (
			!searchParams.get('afid') &&
			typeof qRecipientId === 'string' &&
			qRecipientId.includes('=')
		) {
			trueQuery = new URLSearchParams(qRecipientId);
		}

		const isFromEmail = trueQuery
			? trueQuery.get('utm_medium') === 'email'
			: searchParams.get('utm_medium') === 'email';
		const afid = trueQuery ? trueQuery.get('afid') : searchParams.get('afid');

		if (isFromEmail && afid) {
			dispatch(AppOpenedFromEmailAction(afid));
		}

		handledRef.current = true;

		let nextPathname: string | undefined;

		// Site route, DD landings, landings, emails may have recipientId as page param in pathname
		// Which is additionally presented in `query` object
		// And also may keep `recipientId` as query param
		const recipientId =
			botClientId ||
			(trueQuery && qRecipientId ? qRecipientId.split('&')[0] : qRecipientId);

		if (recipientId) {
			const curBot = bots.find((bot) => bot.clientId === +recipientId);
			const unavailableBot = unavailableBots.find(
				(bot) => bot.clientId === +recipientId
			);
			if ((!curBot || curBot.disabledByLevel) && !unavailableBot) {
				nextPathname = routes.explore.home;
			} else {
				if (searchParams.get(QUERY_TOKEN_PARAM_NAME)) {
					setLastBotId(recipientId);
					nextPathname = routes.chats.dialog(recipientId);

					setNeedsToShowBotPaywall({
						botId: recipientId,
						checkoutReason: CheckoutReason.DigitalCopyLanding,
					});
				}

				if (paywall) {
					if (!isDemoUser) {
						if (paywall === paywallLinkParam.botSubscription) {
							setNeedsToShowBotPaywall({
								botId: recipientId,
								checkoutReason: getPaywallReason(
									CheckoutReason.DigitalCopyDeeplink
								),
							});
						}
						if (paywall === paywallLinkParam.neurons) {
							setNeedsToShowNeuronsPaywall({
								checkoutReason: getPaywallReason(CheckoutReason.Deeplink),
							});
						}
					}

					if (!nextPathname) {
						nextPathname = routes.chats.dialog(recipientId);
					}
				}
			}
		} else if (
			pathname === routes.index ||
			pathname === routes.subscribe.home ||
			pathname === routes.neurons.home
		) {
			if (searchParams.get(QUERY_TOKEN_PARAM_NAME)) {
				if (lastBotId) {
					nextPathname = routes.chats.dialog(lastBotId);
				} else {
					const mainBot = bots.find((bot) => bot.accountType === 'main');
					if (mainBot && mainBot.totalCount < 1) {
						setLastBotId(`${mainBot.clientId}`);
						nextPathname = routes.chats.dialog(mainBot.clientId);
					}
				}
			}

			if (paywall) {
				if (paywall === paywallLinkParam.subscription) {
					setNeedsToShowPaywall({
						checkoutReason: getPaywallReason(CheckoutReason.Deeplink),
					});
				}
				if (paywall === paywallLinkParam.neurons) {
					setNeedsToShowNeuronsPaywall({
						checkoutReason: getPaywallReason(CheckoutReason.Deeplink),
					});
				}

				if (!nextPathname) {
					nextPathname = routes.index;
				}
			}
		} else if (
			pathname === routes.signin.home ||
			pathname === routes.signup.home
		) {
			nextPathname = routes.index;
		}

		handlePromocodeFromQuery(searchParams);

		// Landing after signin/signup and emails links should have authorization `token` query param
		if (
			!searchParams.get(QUERY_TOKEN_PARAM_NAME) &&
			!searchParams.get(QUERY_PROMOCODE_PARAM_NAME) &&
			!nextPathname
		) {
			return;
		}

		saveToSessionStorage(ssKeys.NEED_HIDE_REMINDER, { hide: false });
		fireReminderEvent();

		const query = new NextUrlConstructor(
			nextPathname ?? pathname,
			searchParams
		);

		query
			.delete(QUERY_TOKEN_PARAM_NAME)
			.delete(QUERY_PROMOCODE_PARAM_NAME)
			.delete('recipientId')
			.delete('botId')
			.delete('afid')
			.delete('utm_campaign')
			.delete('utm_source')
			.delete('utm_medium');

		router.replace(query.getUrl());
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [userId, lastBotId, setNeedsToShowBotPaywall, setNeedsToShowPaywall]);
};

export const LandingQuery = ({
	paywall,
}: {
	paywall?: (typeof paywallLinkParam)[keyof typeof paywallLinkParam];
}) => {
	useLandingQuery({ paywall });
	return null;
};
